import { Injectable } from "@angular/core";
import { Data } from "@angular/router";
import {
  buildLocalPushBannerContent,
  resolvePushToastLanguage,
} from "@core/push-notification-toast.i18n";
import { UtilityService } from "@core/utility.service";
import { firstValueFrom, of, Subscription } from "rxjs";
import { catchError, map, take, takeUntil, timeout } from "rxjs/operators";
import { CommonVariable } from "src/app/app.constants";
import {
  resolveInlineCandidateList,
  selectDeviceInlineNotification,
  shouldShowAddHomeScreenBanner,
} from "src/app/components/add-home-screen-banner/add-home-screen.helpers";

/**
 * Encapsulates the inline-notification / feedback logic that previously lived
 * on `StorePage`. Each method receives the host `StorePage` instance so the
 * exact behaviour and state mutations are preserved.
 */
@Injectable()
export class StoreNotificationService {
  private expiryTimer: ReturnType<typeof setTimeout> | undefined;
  private countdownSub: Subscription | undefined;

  validateNotifications(host: any, storeDetails: Data): void {
    if (this.expiryTimer) {
      clearTimeout(this.expiryTimer);
      this.expiryTimer = undefined;
    }

    const candidateList = resolveInlineCandidateList(storeDetails);
    const notification = this.selectActiveInlineNotification(candidateList);

    if (!notification) {
      this.dismissExpiredToRebuild(host, candidateList);
      this.clearCountdownSubscription();
      host.inlineNotificationDetails = {};
      host.countdownSeconds = null;
      return;
    }

    this.applyNotificationDetails(host, notification);
    this.scheduleExpiryRevalidation(host, storeDetails, notification);
  }

  /** Device-local selection from account-neutral candidates (AC1/AC4–AC6/AC9). */
  private selectActiveInlineNotification(inlineNotifications: Data[] | undefined): Data | null {
    return selectDeviceInlineNotification(inlineNotifications as Array<Record<string, unknown>>);
  }

  /**
   * When the page array only has expired items, dismiss the first one so
   * createinlinenotification rebuilds main_pages with the next valid promo.
   */
  private dismissExpiredToRebuild(host: any, inlineNotifications: Data[] | undefined): void {
    if (!Array.isArray(inlineNotifications)) {
      return;
    }

    const now = Date.now();
    for (const notification of inlineNotifications) {
      if (notification?.dismissed) {
        continue;
      }
      const expiryMs = notification?.expiry_date?.seconds
        ? notification.expiry_date.seconds * 1000
        : 0;
      if (!expiryMs || expiryMs <= now) {
        const path = notification?.notification_id?.path;
        if (path) {
          notification.dismissed = true;
          host.notificationsService?.updateClaiminlineVoucherDismiss(path);
        }
        return;
      }
    }
  }

  private applyNotificationDetails(host: any, notification: Data): void {
    host.inlineNotificationDetails = { ...(notification || {}) };
    const inlineId = host.inlineNotificationDetails?.notification_id?.id;
    const expirySeconds = host.inlineNotificationDetails?.expiry_date?.seconds;
    this.clearCountdownSubscription();
    if (expirySeconds == null || !inlineId) {
      return;
    }

    this.countdownSub = host.notificationsService
      ?.startCountdownMonitor(expirySeconds, inlineId)
      ?.pipe(takeUntil(host.destroy$))
      .subscribe((res) => {
        host.countdownSeconds = res;
      });
  }

  private scheduleExpiryRevalidation(host: any, storeDetails: Data, notification: Data): void {
    const expiryMs = notification?.expiry_date?.seconds
      ? notification.expiry_date.seconds * 1000
      : 0;
    if (!expiryMs) {
      return;
    }

    const delay = expiryMs - Date.now();
    const run = () => {
      this.expiryTimer = undefined;
      const next = this.selectActiveInlineNotification(resolveInlineCandidateList(storeDetails));
      if (next) {
        this.applyNotificationDetails(host, next);
        this.scheduleExpiryRevalidation(host, storeDetails, next);
        return;
      }
      const path = notification?.notification_id?.path;
      if (path) {
        notification.dismissed = true;
        host.notificationsService?.updateClaiminlineVoucherDismiss(path);
      }
      this.clearCountdownSubscription();
      host.inlineNotificationDetails = {};
      host.countdownSeconds = null;
    };

    if (delay <= 0) {
      run();
      return;
    }

    this.expiryTimer = setTimeout(run, delay + 100);
  }

  private clearCountdownSubscription(): void {
    this.countdownSub?.unsubscribe();
    this.countdownSub = undefined;
  }

  isPushNotification(host: any): boolean {
    const details = host?.inlineNotificationDetails;
    const id = details?.notification_id?.id || details?.id;
    // Match push-notification-banner detection so Store never falls through to the
    // generic inline slot (which always renders claim line_1).
    if (id === "push_notification") {
      return true;
    }
    const actionDetails = details?.action?.details?.toLowerCase?.() || "";
    return (
      actionDetails === "enable_push_notifications" ||
      actionDetails === "enable push notifications"
    );
  }

  /**
   * After settings language change, re-pick content[lang] from the source
   * inline_notification doc so the Store slot is not stuck on flattened English
   * from main_pages. Push banners fall back to local i18n when content[lang]
   * is missing (Live CDN / flat English seed).
   */
  async resolveInlineContentForLanguage(host: any, language: string): Promise<void> {
    const details = host?.inlineNotificationDetails;
    if (!details?.content || typeof details.content !== "object") {
      return;
    }
    const code = resolvePushToastLanguage(language, null, null);
    const applyLocalPushContent = (): void => {
      if (!this.isPushNotification(host) || code === "en") {
        return;
      }
      host.inlineNotificationDetails = {
        ...details,
        content: { ...buildLocalPushBannerContent(code) },
      };
    };
    const path = details?.notification_id?.path;
    if (!path) {
      applyLocalPushContent();
      return;
    }
    try {
      const snap = await firstValueFrom(
        host.authService.getAngularFireStore(path, "get").pipe(
          take(1),
          timeout({ each: 3000 }),
          catchError(() => of(null))
        )
      );
      const data =
        snap && typeof (snap as { data?: () => Data }).data === "function"
          ? (snap as { data: () => Data }).data()
          : null;
      const contentMap = data?.content;
      if (!contentMap || typeof contentMap !== "object") {
        applyLocalPushContent();
        return;
      }
      const langEntry = contentMap[code];
      const enEntry = contentMap.en;
      const isLangBlock = (entry: unknown): entry is Data =>
        !!entry &&
        typeof entry === "object" &&
        !Array.isArray(entry) &&
        ((entry as Data).title != null ||
          (entry as Data).line_1 != null ||
          (entry as Data).button_label != null);
      if (!isLangBlock(langEntry) && !isLangBlock(enEntry)) {
        // Flat English (or single-lang) main_pages payload — localise push copy.
        applyLocalPushContent();
        return;
      }
      // Prefer requested language. Missing content[lang] for push → local i18n
      // so Store does not keep English claim copy after a language switch.
      if (!isLangBlock(langEntry) && code !== "en") {
        applyLocalPushContent();
        return;
      }
      const resolved = isLangBlock(langEntry)
        ? langEntry
        : isLangBlock(enEntry)
          ? enEntry
          : details.content;
      // Push only: local fills gaps (e.g. missing line_1_alt). Never merge push
      // fallback copy into non-push inline notifications.
      const content = this.isPushNotification(host)
        ? { ...buildLocalPushBannerContent(code), ...resolved }
        : { ...resolved };
      host.inlineNotificationDetails = {
        ...details,
        content,
      };
    } catch (error) {
      applyLocalPushContent();
      host.authService?.errorHandlerLogs?.(
        "Error resolving Store inline notification language",
        error
      );
    }
  }

  isAddHomeScreen(host: any): boolean {
    const details = host?.inlineNotificationDetails?.action?.details;
    const actionDetails = typeof details === "string" ? details.toLowerCase() : undefined;
    return shouldShowAddHomeScreenBanner(
      actionDetails,
      CommonVariable?.device?.operatingSystem,
      UtilityService.isAppInstalledOnHomePage()
    );
  }

  getNotificationDetails(host: any, storeDetails: Data): void {
    const notification = this.selectActiveInlineNotification(resolveInlineCandidateList(storeDetails));
    if (!notification) {
      this.clearCountdownSubscription();
      host.inlineNotificationDetails = {};
      host.countdownSeconds = null;
      return;
    }
    this.applyNotificationDetails(host, notification);
  }

  handleInlineNotification(host: any, notificationDetails: Data): void {
    if (notificationDetails?.action?.type === "nav" && notificationDetails?.action?.details) {
      const actionDetails = notificationDetails.action.details;

      if (actionDetails?.includes("http")) {
        this.openTypeFormForFeedback(host, notificationDetails);
      } else {
        host.route.navigateByUrl(actionDetails)?.catch((error) => error);
      }
      // To dismiss current notification
      this.feedBackDismiss(host);
    }

    if (notificationDetails?.action?.type === "script") {
      host.isVoucherClaimedSignal.set(true);
      host.notificationsService
        .onClaiminlinevoucher(notificationDetails)
        .then((result: Data) => {
          if (result) host.isVoucherClaimedSignal.set(false);
        })
        .catch((error) => {
          host.authService.errorHandlerLogs("Error claiming voucher:", error);
        });
    }
  }

  async openTypeFormForFeedback(host: any, notification: Data): Promise<void> {
    const language = host.languageCodeSignal();

    if (notification?.action?.type === "nav" && !notification?.viewed) {
      if (notification?.action?.details?.includes("typeform.com")) {
        // ✅ Get userId from commonService
        const userId = await firstValueFrom(host.commonService.getUid("AuthGamesService"));
        if (!userId) return;

        const balanceSubscription = host.authService
          .getAngularFireStore(`/users/${userId}/balance/current`, "get")
          .pipe(map((res: Data) => res?.data()))
          .subscribe({
            next: (balance: Data) => {
              if (balance) {
                const credit_balance = balance?.preferred_currency?.credit || null;
                const Point_balance = balance?.points || null;

                const hiddenData = {
                  user_id: userId,
                  Point_balance,
                  Credit_Balance: credit_balance,
                  Language: language || "en",
                  User_State: null,
                };

                const url = notification?.action?.details;
                host.AuthGamesService.typeForm(url, hiddenData, notification);
                balanceSubscription.unsubscribe();
              }
            },
            error: (error) => {
              host.authService.errorHandlerLogs("Failed to get balance:", error);
              balanceSubscription.unsubscribe();
            },
          });
      }
    }
  }

  // This method  used to dismiss dialog
  feedBackDismiss(host: any): void {
    // Path based on the provided type
    const route = host.inlineNotificationDetails?.notification_id?.path;
    if (!route) {
      return;
    }
    const doc_id = route?.substring(route.lastIndexOf("/") + 1) || null;
    const path_id = "pGxBYtRO4mdgNkboYdSy";
    const doc_data = { dismissed: true, viewed: true };
    const dv = { docId: doc_id };
    const method = "update";
    host.authService
      .getV2CloudFunction("updatefirestoredoc", {
        path_id,
        doc_data,
        dv,
        method,
      })
      .subscribe({
        error: (error) => {
          host.authService.errorHandlerLogs("Error updating feedback:", error);
          return error;
        },
      });
  }

  processNotification(host: any, notification: any): void {
    host.notificationData = notification;
    host.showPointsNotification = true;
    host.feedBackTypeFormViewed = notification?.viewed;
  }

  clearExpiryTimer(): void {
    if (this.expiryTimer) {
      clearTimeout(this.expiryTimer);
      this.expiryTimer = undefined;
    }
    this.clearCountdownSubscription();
  }
}
